import { InnerBox } from "@/components/elements/InnerBox";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { BEFORE_ENTRY_LABEL } from "@/features/talent/constants/form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider } from "@/theme/colors";
import { FCC } from "@/types/common";
import { Stack, Grid, Collapse } from "@mui/material";
import dayjs from "dayjs";

type TTalentResidenceStatusInfoProps = {
  defaultValues?: Partial<TTalentForm>;
  /** Hide passport fields for shared/public view */
  isSharedView?: boolean;
};

export const TalentResidenceStatusInfo: FCC<TTalentResidenceStatusInfoProps> = ({ defaultValues, isSharedView = false }) => {
  const { expanded, toggle } = useCollapsible();

  const isBeforeEntry = !!defaultValues?.isBeforeEntry;

  /** 起算日 display: show "未入国・初回申請前" when before-entry, else show formatted date */
  const startDateDisplay = isBeforeEntry
    ? BEFORE_ENTRY_LABEL
    : defaultValues?.specifiedSkillFirstStartDate && dayjs(defaultValues.specifiedSkillFirstStartDate).isValid()
      ? dayjs(defaultValues.specifiedSkillFirstStartDate).format(DATE_FORMAT)
      : "";

  /** 残存期間 display: show "5年 0ヶ月" when before-entry */
  const remainingTermDisplay = isBeforeEntry
    ? "5年 0ヶ月"
    : defaultValues?.specifiedSkillFirstRemainingTerm
      ? `${defaultValues.specifiedSkillFirstRemainingTermYear}年 ${defaultValues.specifiedSkillFirstRemainingTermMonth}ヶ月`
      : "0年0ヶ月";

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="在留資格情報" expanded={expanded} onToggle={toggle} />

      <Collapse in={expanded}>
        <Stack mt={3}>
          <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            <Grid item xs={6}>
              <TalentInfoItem label="在留資格" value={defaultValues?.residenceStatus?.residenceStatus} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem
                label="在留期限"
                value={
                  defaultValues?.periodOfStay && dayjs(defaultValues.periodOfStay).isValid()
                    ? dayjs(defaultValues.periodOfStay).format(DATE_FORMAT)
                    : ""
                }
              />
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={1}>
          <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            {!isSharedView && (
              <Grid item xs={6}>
                <TalentInfoItem label="在留カード番号" value={defaultValues?.cardNumber} />
              </Grid>
            )}
            <Grid item xs={6}>
              <TalentInfoItem label="特定技能1号残存期間" value={remainingTermDisplay} />
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={1}>
         <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            <Grid item xs={6}>
              <TalentInfoItem label="特定技能1号起算日" value={startDateDisplay} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem
                label="ブランク期間（月単位）"
                value={isBeforeEntry ? EMPTY_STR.TEXT : (defaultValues?.blankPeriodMonthly ? defaultValues?.blankPeriodMonthly : EMPTY_STR.TEXT)}
              />
            </Grid>
          </Grid>
        </Stack>

        {!isSharedView && (
          <Stack mt={1} id="passport_code_input">
            <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
              <Grid item xs={6}>
                <TalentInfoItem label="パスポート番号" value={defaultValues?.passportNumber} isShowDivider={false} />
              </Grid>
              <Grid item xs={6}>
                <TalentInfoItem
                  label="パスポート期限"
                  value={
                    defaultValues?.passportExpiryDate && dayjs(defaultValues.passportExpiryDate).isValid()
                      ? dayjs(defaultValues.passportExpiryDate).format(DATE_FORMAT)
                      : ""
                  }
                  isShowDivider={false}
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </Collapse>
    </InnerBox>
  );
};
